/**
 * Pure command-list logic for the `CommandPalette` (T2.2) — no React, so
 * ranking/grouping/filtering can be unit-tested directly without rendering
 * anything. `CommandPalette.tsx` turns each `CommandAction` into a real side
 * effect (navigate, toggle theme, sign out, open a URL).
 */

export type NavCommandAction = { type: 'navigate'; to: string };
export type ToggleThemeCommandAction = { type: 'toggle-theme' };
export type SignOutCommandAction = { type: 'sign-out' };
export type OpenUrlCommandAction = { type: 'open-url'; url: string };

export type CommandAction =
  | NavCommandAction
  | ToggleThemeCommandAction
  | SignOutCommandAction
  | OpenUrlCommandAction;

export type CommandGroupName = 'Pages' | 'Actions' | 'Commands';

export type CommandEntry = {
  id: string;
  label: string;
  group: CommandGroupName;
  keywords?: string[];
  action: CommandAction;
};

export type RankedCommandGroup = {
  group: CommandGroupName;
  entries: CommandEntry[];
};

type NavEntryLike = { to: string; title: string };

/** A scaffolder Template entity, reduced to what the palette needs. */
type TemplateLike = {
  namespace: string;
  name: string;
  title: string;
};

export type BuildCommandEntriesInput = {
  navEntries: NavEntryLike[];
  templates: TemplateLike[];
  /** Omitted when the org's web URL can't be resolved from config. */
  githubOrgUrl?: string;
};

const GROUP_ORDER: CommandGroupName[] = ['Pages', 'Actions', 'Commands'];

/**
 * The scaffolder's own selected-template route, spelled out directly rather
 * than via `useRouteRef(scaffolderPlugin.routes.selectedTemplate)` — that
 * hook needs the full app extension tree, which would force every test that
 * renders the palette onto `renderInTestApp`, and `renderInTestApp`'s baked-in
 * guest-identity fallback can't be overridden by a test's `identityApiRef`
 * mock (see `/memories/repo/vivicta-developer-portal.md`). Matches
 * `TemplatePickerPage.tsx`'s own resolved hrefs.
 */
export const templateHref = (namespace: string, name: string): string =>
  `/create/templates/${namespace}/${name}`;

export function buildCommandEntries({
  navEntries,
  templates,
  githubOrgUrl,
}: BuildCommandEntriesInput): CommandEntry[] {
  const pages: CommandEntry[] = navEntries.map(entry => ({
    id: `page:${entry.to}`,
    label: entry.title,
    group: 'Pages',
    action: { type: 'navigate', to: entry.to },
  }));

  const create: CommandEntry[] = templates.map(template => ({
    id: `template:${template.namespace}/${template.name}`,
    label: template.title,
    group: 'Actions',
    keywords: ['action', 'create', 'template', 'new'],
    action: { type: 'navigate', to: templateHref(template.namespace, template.name) },
  }));

  const actions: CommandEntry[] = [
    {
      id: 'action:toggle-theme',
      label: 'Toggle theme',
      group: 'Commands',
      keywords: ['dark', 'light', 'appearance'],
      action: { type: 'toggle-theme' },
    },
    ...(githubOrgUrl
      ? [
          {
            id: 'action:open-github-org',
            label: 'Open GitHub org',
            group: 'Commands' as const,
            keywords: ['github', 'repository', 'org'],
            action: { type: 'open-url', url: githubOrgUrl } as const,
          },
        ]
      : []),
    {
      id: 'action:sign-out',
      label: 'Sign out',
      group: 'Commands',
      keywords: ['logout', 'log out'],
      action: { type: 'sign-out' },
    },
  ];

  return [...pages, ...create, ...actions];
}

/** Exact label match > prefix match > substring match (label or keywords). */
function matchScore(entry: CommandEntry, needle: string): number | undefined {
  const label = entry.label.toLowerCase();
  if (label === needle) return 3;
  if (label.startsWith(needle)) return 2;
  if (label.includes(needle)) return 1;
  if (entry.keywords?.some(keyword => keyword.toLowerCase().includes(needle))) {
    return 1;
  }
  return undefined;
}

export function rankCommandEntries(
  entries: CommandEntry[],
  query: string,
): CommandEntry[] {
  const needle = query.trim().toLowerCase();

  if (!needle) {
    return [...entries].sort((a, b) => a.label.localeCompare(b.label));
  }

  return entries
    .map(entry => ({ entry, score: matchScore(entry, needle) }))
    .filter(
      (scored): scored is { entry: CommandEntry; score: number } =>
        scored.score !== undefined,
    )
    .sort(
      (a, b) => b.score - a.score || a.entry.label.localeCompare(b.entry.label),
    )
    .map(scored => scored.entry);
}

/** Drops empty groups so the palette doesn't render a heading with nothing under it. */
export function groupCommandEntries(entries: CommandEntry[]): RankedCommandGroup[] {
  return GROUP_ORDER.map(group => ({
    group,
    entries: entries.filter(entry => entry.group === group),
  })).filter(ranked => ranked.entries.length > 0);
}

export function buildCommandGroups(
  entries: CommandEntry[],
  query: string,
): RankedCommandGroup[] {
  return groupCommandEntries(rankCommandEntries(entries, query));
}
